import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import CardsGroup from '../../components/dynamicFieldCards/CardsGroup';
import NestedCardsGroup from '../../components/dynamicFieldCards/nested/CardsGroup';
import ArrayCardsGroup from '../../components/dynamicFieldCards/arrayOfTextInputs/CardsGroup';
import CustomLabels from '../../components/CustomLabels';
import { showToast } from '../../components/ToastConfig';

import NewCompanyService from '../../services/company-service';

// 第一頁：公司基本資料
const step1Schema = z.object({
  name: z.string().min(2, '公司名稱至少需要 2 個字元').max(50, '公司名稱最多 50 個字元'),
  legal_name: z.string().min(4, '公司法律名稱至少需要 4 個字元').max(200, '公司法律名稱最多 200 個字元'),
  // about: z.string().transform(val => val || undefined).pipe(
  //   z.string().min(20, '公司簡介至少需要 20 個字元').max(200, '公司簡介最多 200 個字元').optional()
  // ),
  // full_description: z.string().transform(val => val || undefined).pipe(
  //   z.string().min(100, '公司詳細描述至少需要 100 個字元').max(1000, '公司詳細描述最多 1000 個字元').optional()
  // ),
  // address: z.string().transform(val => val || undefined).pipe(
  //   z.string().min(8, '公司地址至少需要 8 個字元').max(200, '公司地址最多 200 個字元').optional()
  // ),
  // contact_email: z.string().transform(val => val || undefined).pipe(
  //   z.string().email('請輸入有效的 Email').max(50, 'Email 最多 50 個字元').optional()
  // ),
  contact_email: z.preprocess(val => val === '' ? null : val, z.string().email('請輸入有效的 Email').max(50, 'Email 最多 50 個字元').nullable().transform(val => val || undefined)),
  // contact_phone: z.string().transform(val => val || undefined).pipe(
  //   z.string().min(8, '聯絡電話至少需要 8 個字元').max(50, '聯絡電話最多 50 個字元').optional()
  // ),
  // website: z.string().transform(val => val || undefined).pipe(
  //   z.string().min(10, '官網網址至少需要 10 個字元').max(100, '官網網址最多 100 個字元').optional()
  // ),
});

// 第二頁：公司股份相關資料
const step2Schema = z.object({
  stock_symbol: z.string().transform(val => val || undefined).pipe(
    z.string().min(3, '公司股票代碼至少需要 3 個字元').max(20, '公司股票代碼最多 20 個字元').optional()
  ),
  company_type: z.string().transform(val => val || undefined).pipe(
    z.string().min(2, '公司營利類型至少需要 2 個字元').max(50, '公司營利類型最多 50 個字元').optional()
  ),
  operating_status: z.string().transform(val => val || undefined).pipe(
    z.string().min(2, '公司營運狀態至少需要 2 個字元').max(50, '公司類型最多 50 個字元').optional()
  ),
  ipo_status: z.string().transform(val => val || undefined).pipe(
    z.string().min(2, '首次公開募股狀態至少需要 2 個字元').max(50, '首次公開募股狀態最多 50 個字元').optional()
  ),
  country_code: z.string().transform(val => val || undefined).pipe(
    z.string().min(2, '國家代碼至少需要 2 個字元').max(20, '首次公開募股狀態最多 20 個字元').optional()
  ),
  type: z.string().transform(val => val || undefined).pipe(
    z.string().min(2, '公司類型至少需要 2 個字元').max(50, '公司類型最多 50 個字元').optional()
  ),
  cb_rank: z.string().transform(val => val || undefined).pipe(
    z.string()
      .regex(/^\d+$/, "請輸入數字")
      .transform((val) => Number(val))
      .pipe(z.number().min(1, "排行最小為 1"))
      .optional()
  ),
});

// 第三頁：員工及公司股份等偏數字方面的資料
const step3Schema = z.object({
  num_employees: z.string().transform(val => val || undefined).pipe(
    z.string()
      .regex(/^\d+$/, "請輸入數字")
      .transform((val) => Number(val))
      .pipe(z.number().min(0, "員工人數最少為 0"))
      .optional()
  ),
  num_acquisitions: z.string().transform(val => val || undefined).pipe(
    z.string()
      .regex(/^\d+$/, "請輸入數字")
      .transform((val) => Number(val))
      .pipe(z.number().min(0, "數量最少為 0"))
      .optional()
  ),
  num_investments: z.string().transform(val => val || undefined).pipe(
    z.string()
      .regex(/^\d+$/, "請輸入數字")
      .transform((val) => Number(val))
      .pipe(z.number().min(0, "投資數量最少為 0"))
      .optional()
  ),
  num_exits: z.string().transform(val => val || undefined).pipe(
    z.string()
      .regex(/^\d+$/, "請輸入數字")
      .transform((val) => Number(val))
      .pipe(z.number().min(0, "數量最少為 0"))
      .optional()
  ),
  num_sub_organizations: z.string().transform(val => val || undefined).pipe(
    z.string()
      .regex(/^\d+$/, "請輸入數字")
      .transform((val) => Number(val))
      .pipe(z.number().min(0, "子組織數量最少為 0"))
      .optional()
  ),
  num_diversity_spotlight_investments: z.string().transform(val => val || undefined).pipe(
    z.string()
      .regex(/^\d+$/, "請輸入數字")
      .transform((val) => Number(val))
      .pipe(z.number().min(0, "數量最少為 0"))
      .optional()
  ),
  num_employee_profiles: z.string().transform(val => val || undefined).pipe(
    z.string()
      .regex(/^\d+$/, "請輸入數字")
      .transform((val) => Number(val))
      .pipe(z.number().min(0, "員工個人檔案數量最少為 0"))
      .optional()
  ),
  num_investments_lead: z.string().transform(val => val || undefined).pipe(
    z.string()
      .regex(/^\d+$/, "請輸入數字")
      .transform((val) => Number(val))
      .pipe(z.number().min(0, "數量最少為 0"))
      .optional()
  ),
  num_investors: z.string().transform(val => val || undefined).pipe(
    z.string()
      .regex(/^\d+$/, "請輸入數字")
      .transform((val) => Number(val))
      .pipe(z.number().min(0, "投資者人數最少為 0"))
      .optional()
  ),
  num_funding_rounds: z.string().transform(val => val || undefined).pipe(
    z.string()
      .regex(/^\d+$/, "請輸入數字")
      .transform((val) => Number(val))
      .pipe(z.number().min(0, "回合數最少為 0"))
      .optional()
  ),
  founded_date: z.string().transform(val => val || undefined).pipe(
    z.string().min(4, '成立日期至少需要 4 個字元').optional()
  ),
  funding_rounds: z.string().transform(val => val || undefined).pipe(
    z.string()
      .regex(/^\d+$/, "請輸入數字")
      .transform((val) => Number(val))
      .pipe(z.number().min(0, "回合數最少為 0"))
      .optional()
  ),
  ipo_fields: z.object({
    date: z.string().transform(val => val || undefined).pipe(
      z.string().min(4, '日期至少需要 4 個字元').optional()
    ),
  }).optional(),
  people_highlights: z.object({
    num_current_advisor_positions: z.string().transform(val => val || undefined).pipe(
      z.string()
        .regex(/^\d+$/, "請輸入數字")
        .transform((val) => Number(val))
        .pipe(z.number().min(0, "目前顧問人數最少為 0"))
        .optional()
    ),
  }).optional(),
});

// 第四頁：使用者可新增欄位的資料 - 1
const step4Schema = z.object({
  // 單層物件 schema
  location: z.array(
    z.object({
      name: z.string().transform(val => val || undefined).pipe(
        z.string().min(2, '名稱至少需要 2 個字元').optional()
      ),
      permalink: z.string().transform(val => val || undefined).pipe(
        z.string().min(2, '連結至少需要 2 個字元').optional()
      )
    })
  ),
  industries: z.array(
    z.object({
      id: z.string().transform(val => val || undefined).pipe(
        z.string().min(1, 'ID 為必填').optional()
      ),
      value: z.string().transform(val => val || undefined).pipe(
        z.string().min(2, '名稱至少需要 2 個字元').optional()
      )
    })
  ),
  founders: z.array(
    z.object({
      value: z.string().transform(val => val || undefined).pipe(
        z.string().min(2, '姓名至少需要 2 個字元').optional()
      )
    })
  ),
  headquarters_regions: z.array(
    z.object({
      id: z.string().transform(val => val || undefined).pipe(
        z.string().min(1, 'ID 為必填').optional()
      ),
      value: z.string().transform(val => val || undefined).pipe(
        z.string().min(2, '名稱至少需要 2 個字元').optional()
      )
    })
  ),
  investors: z.array(
    z.object({
      id: z.string().min(1, 'ID 為必填'),
      type: z.string().min(2, '類型至少需要 2 個字元'),
      value: z.string().min(2, '名稱至少需要 2 個字元'),
      lead_investor: z.boolean().default(false)
    })
  )
});

// 第五頁：使用者可新增欄位的資料 - 2
const step5Schema = z.object({
  // 多層物件 schema
  funding_rounds_list: z.array(
    z.object({
      announced_on: z.string().min(2, '日期為必填'),
      lead_investors: z.array(
        z.object({
          image: z.string().min(2, '圖片連結至少需要 2 個字元'),
          name: z.string().min(2, '投資者名稱至少需要 2 個字元'),
          permalink: z.string().min(2, '永久連結至少需要 2 個字元')
        })
      ),
      id: z.string().min(1, 'ID 為必填'),
      image_id: z.string().min(1, '圖片 ID 為必填'),
      title: z.string().min(2, '標題至少需要 2 個字元'),
      uuid: z.string().min(1, 'UUID 為必填'),
      money_raised: z.object({
        value_usd: z.string().transform(val => val || undefined).pipe(
          z.string()
            .regex(/^\d+$/, "請輸入數字")
            .transform((val) => Number(val))
            .pipe(z.number().min(0, "金額不能小於 0"))
            .optional()
        ),
        currency: z.string().min(2, '幣別至少需要 2 個字元'),
        value: z.string().transform(val => val || undefined).pipe(
          z.string()
            .regex(/^\d+$/, "請輸入數字")
            .transform((val) => Number(val))
            .pipe(z.number().min(0, "金額不能小於 0"))
            .optional()
        )
      })
    })
  ),
});

const step6Schema = z.object({
  investments: z.array(
    z.object({
      announced_on: z.string().min(2, '公告日期至少需要 2 個字元'),
      organization: z.object({
        id: z.string().min(1, 'ID 為必填'),
        image_id: z.string().min(1, '圖片 ID 為必填'),
        type: z.string().min(2, '類型至少需要 2 個字元'),
        value: z.string().min(2, '名稱至少需要 2 個字元')
      }),
      funding_round: z.object({
        id: z.string().min(1, 'ID 為必填'),
        image_id: z.string().min(1, '圖片 ID 為必填'),
        type: z.string().min(2, '類型至少需要 2 個字元'),
        value: z.string().min(2, '名稱至少需要 2 個字元')
      }),
      id: z.string().min(1, 'ID 為必填'),
      type: z.string().min(2, '類型至少需要 2 個字元'),
      value: z.string().min(2, '金額至少需要 2 個字元'),
      lead_investor: z.boolean().default(false)
    })
  )
});

const step7Schema = z.object({
  diversity_investments: z.array(
    z.object({
      announced_on: z.string().min(2, '公告日期至少需要 2 個字元'),
      organization: z.object({
        id: z.string().min(1, 'ID 為必填'),
        image_id: z.string().min(1, '圖片 ID 為必填'),
        type: z.string().min(2, '類型至少需要 2 個字元'),
        value: z.string().min(2, '名稱至少需要 2 個字元')
      }),
      funding_round: z.object({
        id: z.string().min(1, 'ID 為必填'),
        image_id: z.string().min(1, '圖片 ID 為必填'),
        type: z.string().min(2, '類型至少需要 2 個字元'),
        value: z.string().min(2, '名稱至少需要 2 個字元')
      }),
      id: z.string().min(1, 'ID 為必填'),
      type: z.string().min(2, '類型至少需要 2 個字元'),
      value: z.string().min(2, '金額至少需要 2 個字元')
    })
  )
});

const step8Schema = z.object({
  acquisitions: z.array(
    z.object({
      identifier: z.object({
        entity_def_id: z.string().min(1, 'entity_def_id 為必填'),
        image_id: z.string().min(1, 'image_id 為必填'),
        permalink: z.string().min(1, 'permalink 為必填'),
        uuid: z.string().min(1, 'uuid 為必填'),
        value: z.string().min(2, 'value 至少需要 2 個字元')
      }),
      price: z.object({
        value_usd: z.string().transform(val => val || undefined).pipe(
          z.string()
            .regex(/^\d+$/, "請輸入數字")
            .transform((val) => Number(val))
            .pipe(z.number().min(0, "金額不能小於 0"))
        ),
        value: z.string().transform(val => val || undefined).pipe(
          z.string()
            .regex(/^\d+$/, "請輸入數字")
            .transform((val) => Number(val))
            .pipe(z.number().min(0, "金額不能小於 0"))
        )
      }),
      announced_on: z.object({
        value: z.string().min(1, '日期為必填')
      }),
      acquiree_identifier: z.object({
        role: z.string().min(1, 'role 為必填'),
        entity_def_id: z.string().min(1, 'entity_def_id 為必填'),
        image_id: z.string().min(1, 'image_id 為必填'),
        permalink: z.string().min(1, 'permalink 為必填'),
        uuid: z.string().min(1, 'uuid 為必填'),
        value: z.string().min(2, 'value 至少需要 2 個字元')
      })
    })
  )
});

const step9Schema = z.object({
  exits: z.array(
    z.object({
      image: z.string().url('請輸入有效的網址'),
      link: z.string().url('請輸入有效的網址'),
      description: z.string().min(2, '描述至少需要 2 個字元'),
      value: z.string().min(2, '類型至少需要 2 個字元')
    })
  ),
  featured_list: z.array(
    z.object({
      title: z.string().min(2, '標題至少需要 2 個字元'),
      org_num: z.string().transform(val => val || undefined).pipe(
        z.string()
          .regex(/^\d+$/, "請輸入數字")
          .transform((val) => Number(val))
          .pipe(z.number().min(0, "數量不能小於 0"))
      ),
      org_num_investors: z.string().transform(val => val || undefined).pipe(
        z.string()
          .regex(/^\d+$/, "請輸入數字")
          .transform((val) => Number(val))
          .pipe(z.number().min(0, "數量不能小於 0"))
      ),
      org_funding_total: z.object({
        currency: z.string().min(2, '幣別至少需要 2 個字元'),
        value: z.string().transform(val => val || undefined).pipe(
          z.string()
            .regex(/^\d+$/, "請輸入數字")
            .transform((val) => Number(val))
            .pipe(z.number().min(0, "金額不能小於 0"))
        ),
        value_usd: z.string().transform(val => val || undefined).pipe(
          z.string()
            .regex(/^\d+$/, "請輸入數字")
            .transform((val) => Number(val))
            .pipe(z.number().min(0, "金額不能小於 0"))
        )
      })
    })
  ),
  contacts: z.array(
    z.object({
      name: z.string().min(2, '姓名至少需要 2 個字元'),
      job_title: z.string().min(2, '職稱至少需要 2 個字元'),
      linkedin_id: z.string().min(2, 'LinkedIn ID 至少需要 2 個字元'),
      departments: z.array(z.string()).min(1, '至少需要選擇一個部門'),
      levels: z.array(z.string()).min(1, '至少需要選擇一個職級')
    })
  ),
  financials_highlights: z.array(
    z.object({
      num_lead_investors: z.string().transform(val => val || undefined).pipe(
        z.string()
          .regex(/^\d+$/, "請輸入數字")
          .transform((val) => Number(val))
          .pipe(z.number().min(0, "數量不能小於 0"))
      ),
      num_investors: z.string().transform(val => val || undefined).pipe(
        z.string()
          .regex(/^\d+$/, "請輸入數字")
          .transform((val) => Number(val))
          .pipe(z.number().min(0, "數量不能小於 0"))
      ),
      num_funding_rounds: z.string().transform(val => val || undefined).pipe(
        z.string()
          .regex(/^\d+$/, "請輸入數字")
          .transform((val) => Number(val))
          .pipe(z.number().min(0, "數量不能小於 0"))
      )
    })
  )
});

// 合併所有 schema
const formSchema = z.object({
  ...step1Schema.shape,
  // ...step2Schema.shape,
  // ...step3Schema.shape,
  // ...step4Schema.shape,
  // ...step5Schema.shape,
  // ...step6Schema.shape,
  // ...step7Schema.shape,
  // ...step8Schema.shape,
  // ...step9Schema.shape,
});


// 各步驟的表單元件
const Step1Form = ({ register, errors }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xxl:grid-cols-4 gap-4">
    <div className="space-y-1">
      <label className="block text-sm font-medium">name</label>
      <input
        {...register('name')}
        className={`w-full p-2 border rounded ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
      />
      {errors.name && (
        <p className="text-red-500 text-sm">{errors.name.message}</p>
      )}
    </div>
    <div className="space-y-1">
      <label className="block text-sm font-medium">legal_name</label>
      <input
        {...register('legal_name')}
        className={`w-full p-2 border rounded ${errors.legal_name ? 'border-red-500' : 'border-gray-300'}`}
      />
      {errors.legal_name && (
        <p className="text-red-500 text-sm">{errors.legal_name.message}</p>
      )}
    </div>
    <div className="space-y-1">
      <label className="block text-sm font-medium">about</label>
      <input
        {...register('about')}
        className={`w-full p-2 border rounded ${errors.about ? 'border-red-500' : 'border-gray-300'}`}
      />
      {errors.about && (
        <p className="text-red-500 text-sm">{errors.about.message}</p>
      )}
    </div>
    <div className="space-y-1">
      <label className="block text-sm font-medium">full_description</label>
      <input
        {...register('full_description')}
        className={`w-full p-2 border rounded ${errors.full_description ? 'border-red-500' : 'border-gray-300'}`}
      />
      {errors.full_description && (
        <p className="text-red-500 text-sm">{errors.full_description.message}</p>
      )}
    </div>
    <div className="space-y-1">
      <label className="block text-sm font-medium">address</label>
      <input
        {...register('address')}
        className={`w-full p-2 border rounded ${errors.address ? 'border-red-500' : 'border-gray-300'}`}
      />
      {errors.address && (
        <p className="text-red-500 text-sm">{errors.address.message}</p>
      )}
    </div>
    <div className="space-y-1">
      <label className="block text-sm font-medium">contact_email</label>
      <input
        {...register('contact_email')}
        className={`w-full p-2 border rounded ${errors.contact_email ? 'border-red-500' : 'border-gray-300'}`}
      />
      {errors.contact_email && (
        <p className="text-red-500 text-sm">{errors.contact_email.message}</p>
      )}
    </div>
    <div className="space-y-1">
      <label className="block text-sm font-medium">contact_phone</label>
      <input
        {...register('contact_phone')}
        className={`w-full p-2 border rounded ${errors.contact_phone ? 'border-red-500' : 'border-gray-300'}`}
      />
      {errors.contact_phone && (
        <p className="text-red-500 text-sm">{errors.contact_phone.message}</p>
      )}
    </div>
    <div className="space-y-1">
      <label className="block text-sm font-medium">website</label>
      <input
        {...register('website')}
        className={`w-full p-2 border rounded ${errors.website ? 'border-red-500' : 'border-gray-300'}`}
      />
      {errors.website && (
        <p className="text-red-500 text-sm">{errors.website.message}</p>
      )}
    </div>
  </div>
);

const Step2Form = ({ register, errors }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xxl:grid-cols-4 gap-4">
    <div className="space-y-1">
      <label className="block text-sm font-medium">stock_symbol</label>
      <input
        {...register('stock_symbol')}
        className={`w-full p-2 border rounded ${errors.stock_symbol ? 'border-red-500' : 'border-gray-300'}`}
      />
      {errors.stock_symbol && (
        <p className="text-red-500 text-sm">{errors.stock_symbol.message}</p>
      )}
    </div>
    <div className="space-y-1">
      <label className="block text-sm font-medium">company_type</label>
      <input
        {...register('company_type')}
        className={`w-full p-2 border rounded ${errors.company_type ? 'border-red-500' : 'border-gray-300'}`}
      />
      {errors.company_type && (
        <p className="text-red-500 text-sm">{errors.company_type.message}</p>
      )}
    </div>
    <div className="space-y-1">
      <label className="block text-sm font-medium">operating_status</label>
      <input
        {...register('operating_status')}
        className={`w-full p-2 border rounded ${errors.operating_status ? 'border-red-500' : 'border-gray-300'}`}
      />
      {errors.operating_status && (
        <p className="text-red-500 text-sm">{errors.operating_status.message}</p>
      )}
    </div>
    <div className="space-y-1">
      <label className="block text-sm font-medium">ipo_status</label>
      <input
        {...register('ipo_status')}
        className={`w-full p-2 border rounded ${errors.ipo_status ? 'border-red-500' : 'border-gray-300'}`}
      />
      {errors.ipo_status && (
        <p className="text-red-500 text-sm">{errors.ipo_status.message}</p>
      )}
    </div>
    <div className="space-y-1">
      <label className="block text-sm font-medium">country_code</label>
      <input
        {...register('country_code')}
        className={`w-full p-2 border rounded ${errors.country_code ? 'border-red-500' : 'border-gray-300'}`}
      />
      {errors.country_code && (
        <p className="text-red-500 text-sm">{errors.country_code.message}</p>
      )}
    </div>
    <div className="space-y-1">
      <label className="block text-sm font-medium">type</label>
      <input
        {...register('type')}
        className={`w-full p-2 border rounded ${errors.type ? 'border-red-500' : 'border-gray-300'}`}
      />
      {errors.type && (
        <p className="text-red-500 text-sm">{errors.type.message}</p>
      )}
    </div>
    <div className="space-y-1">
      <label className="block text-sm font-medium">cb_rank</label>
      <input
        {...register('cb_rank')}
        className={`w-full p-2 border rounded ${errors.cb_rank ? 'border-red-500' : 'border-gray-300'}`}
      />
      {errors.cb_rank && (
        <p className="text-red-500 text-sm">{errors.cb_rank.message}</p>
      )}
    </div>
  </div>
);

const Step3Form = ({ register, errors }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xxl:grid-cols-4 gap-4">
    <div className="space-y-1">
      <label className="block text-sm font-medium">num_employees</label>
      <input
        {...register('num_employees')}
        className={`w-full p-2 border rounded ${errors.num_employees ? 'border-red-500' : 'border-gray-300'}`}
      />
      {errors.num_employees && (
        <p className="text-red-500 text-sm">{errors.num_employees.message}</p>
      )}
    </div>
    <div className="space-y-1">
      <label className="block text-sm font-medium">num_acquisitions</label>
      <input
        {...register('num_acquisitions')}
        className={`w-full p-2 border rounded ${errors.num_acquisitions ? 'border-red-500' : 'border-gray-300'}`}
      />
      {errors.num_acquisitions && (
        <p className="text-red-500 text-sm">{errors.num_acquisitions.message}</p>
      )}
    </div>
    <div className="space-y-1">
      <label className="block text-sm font-medium">num_investments</label>
      <input
        {...register('num_investments')}
        className={`w-full p-2 border rounded ${errors.num_investments ? 'border-red-500' : 'border-gray-300'}`}
      />
      {errors.num_investments && (
        <p className="text-red-500 text-sm">{errors.num_investments.message}</p>
      )}
    </div>
    <div className="space-y-1">
      <label className="block text-sm font-medium">num_exits</label>
      <input
        {...register('num_exits')}
        className={`w-full p-2 border rounded ${errors.num_exits ? 'border-red-500' : 'border-gray-300'}`}
      />
      {errors.num_exits && (
        <p className="text-red-500 text-sm">{errors.num_exits.message}</p>
      )}
    </div>
    <div className="space-y-1">
      <label className="block text-sm font-medium">num_sub_organizations</label>
      <input
        {...register('num_sub_organizations')}
        className={`w-full p-2 border rounded ${errors.num_sub_organizations ? 'border-red-500' : 'border-gray-300'}`}
      />
      {errors.num_sub_organizations && (
        <p className="text-red-500 text-sm">{errors.num_sub_organizations.message}</p>
      )}
    </div>
    <div className="space-y-1">
      <label className="block text-sm font-medium">num_diversity_spotlight_investments</label>
      <input
        {...register('num_diversity_spotlight_investments')}
        className={`w-full p-2 border rounded ${errors.num_diversity_spotlight_investments ? 'border-red-500' : 'border-gray-300'}`}
      />
      {errors.num_diversity_spotlight_investments && (
        <p className="text-red-500 text-sm">{errors.num_diversity_spotlight_investments.message}</p>
      )}
    </div>
    <div className="space-y-1">
      <label className="block text-sm font-medium">num_employee_profiles</label>
      <input
        {...register('num_employee_profiles')}
        className={`w-full p-2 border rounded ${errors.num_employee_profiles ? 'border-red-500' : 'border-gray-300'}`}
      />
      {errors.num_employee_profiles && (
        <p className="text-red-500 text-sm">{errors.num_employee_profiles.message}</p>
      )}
    </div>
    <div className="space-y-1">
      <label className="block text-sm font-medium">num_investments_lead</label>
      <input
        {...register('num_investments_lead')}
        className={`w-full p-2 border rounded ${errors.num_investments_lead ? 'border-red-500' : 'border-gray-300'}`}
      />
      {errors.num_investments_lead && (
        <p className="text-red-500 text-sm">{errors.num_investments_lead.message}</p>
      )}
    </div>
    <div className="space-y-1">
      <label className="block text-sm font-medium">num_investors</label>
      <input
        {...register('num_investors')}
        className={`w-full p-2 border rounded ${errors.num_investors ? 'border-red-500' : 'border-gray-300'}`}
      />
      {errors.num_investors && (
        <p className="text-red-500 text-sm">{errors.num_investors.message}</p>
      )}
    </div>
    <div className="space-y-1">
      <label className="block text-sm font-medium">num_funding_rounds</label>
      <input
        {...register('num_funding_rounds')}
        className={`w-full p-2 border rounded ${errors.num_funding_rounds ? 'border-red-500' : 'border-gray-300'}`}
      />
      {errors.num_funding_rounds && (
        <p className="text-red-500 text-sm">{errors.num_funding_rounds.message}</p>
      )}
    </div>
    <div className="space-y-1">
      <label className="block text-sm font-medium">founded_date</label>
      <input
        {...register('founded_date')}
        className={`w-full p-2 border rounded ${errors.founded_date ? 'border-red-500' : 'border-gray-300'}`}
      />
      {errors.founded_date && (
        <p className="text-red-500 text-sm">{errors.founded_date.message}</p>
      )}
    </div>
    <div className="space-y-1">
      <label className="block text-sm font-medium">funding_rounds{` >> `}last_funding_type</label>
      <input
        {...register('funding_rounds')}
        className={`w-full p-2 border rounded ${errors.funding_rounds ? 'border-red-500' : 'border-gray-300'}`}
      />
      {errors.funding_rounds && (
        <p className="text-red-500 text-sm">{errors.funding_rounds.message}</p>
      )}
    </div>
    <div className="space-y-1">
      <label className="block text-sm font-medium">ipo_fields{` >> `}date</label>
      <input
        {...register('ipo_fields.date')} // 使用點號來表示巢狀結構
        className={`w-full p-2 border rounded ${errors.ipo_fields?.date ? 'border-red-500' : 'border-gray-300'}`}
      />
      {errors.ipo_fields?.date && (
        <p className="text-red-500 text-sm">{errors.ipo_fields?.date.message}</p>
      )}
    </div>
    <div className="space-y-1">
      <label className="block text-sm font-medium">people_highlights{` >> `}num_current_advisor_positions</label>
      <input
        {...register('people_highlights.num_current_advisor_positions')}
        className={`w-full p-2 border rounded ${errors.people_highlights?.num_current_advisor_positions ? 'border-red-500' : 'border-gray-300'}`}
      />
      {errors.people_highlights?.num_current_advisor_positions && (
        <p className="text-red-500 text-sm">{errors.people_highlights?.num_current_advisor_positions.message}</p>
      )}
    </div>
  </div>
);

const Step4Form = ({ register, errors, setValue, watch }) => {
  const result = watch('section4Schema') || [];

  const locationFields = [
    { name: 'name', label: 'name' },
    { name: 'permalink', label: 'permalink' },
  ];

  const industryFields = [
    { name: 'id', label: 'id' },
    { name: 'value', label: 'value' }
  ];

  const founderFields = [
    { name: 'value', label: 'value' }
  ];

  const headerQuarterFields = [
    { name: 'id', label: 'id' },
    { name: 'value', label: 'value' }
  ];

  const investorFields = [
    { name: 'id', label: 'id' },
    { name: 'type', label: 'type' },
    { name: 'value', label: 'value' },
    { name: 'lead_investor', label: 'lead_investor', type: 'boolean' }
  ];
  
  return (  
    <div className="space-y-8">
      <div className='border-b pb-8 border-gray-200'>
        <CustomLabels texts="location" />
        <CardsGroup
          register={register}
          errors={errors}
          setValue={setValue}
          watch={watch}
          groupName="location"
          fields={locationFields}
          fieldTitle="location"
        />
      </div>
      <div className='border-b pb-8 border-gray-200'>
        <CustomLabels texts="industries" />
        <CardsGroup
          register={register}
          errors={errors}
          setValue={setValue}
          watch={watch}
          groupName="industries"
          fields={industryFields}
          fieldTitle="industries"
        />
      </div>
      <div className='border-b pb-8 border-gray-200'>
        <CustomLabels texts="founders" />
        <CardsGroup
          register={register}
          errors={errors}
          setValue={setValue}
          watch={watch}
          groupName="founders"
          fields={founderFields}
          fieldTitle="founders"
        />
      </div>
      <div className=''>
        <CustomLabels texts="headquarters_regions" />
        <CardsGroup
          register={register}
          errors={errors}
          setValue={setValue}
          watch={watch}
          groupName="headquarters_regions"
          fields={headerQuarterFields}
          fieldTitle="headquarters_regions"
        />
      </div>
      <div>
        <CustomLabels texts="investors" />
        <CardsGroup
          register={register}
          errors={errors}
          setValue={setValue}
          watch={watch}
          groupName="investors"
          fields={investorFields}
          fieldTitle="investors"
        />
      </div>
    </div>
  );
};

const Step5Form = ({ register, errors, setValue, watch }) => {
  // 定義主要欄位
  const fundingRoundFields = [
    { name: 'announced_on', label: 'announced_on', type: 'text' },
    // { name: 'lead_investors', label: '主要投資者', type: 'nested-array' },
    { 
      name: 'lead_investors', 
      label: 'lead_investors', 
      type: 'nested-array',
      fields: [
        { name: 'image', label: 'image', type: 'text' },
        { name: 'name', label: 'name', type: 'text' },
        { name: 'permalink', label: 'permalink', type: 'text' }
      ]
    },
    { name: 'id', label: 'id', type: 'text' },
    { name: 'image_id', label: 'image_id', type: 'text' },
    { name: 'title', label: 'title', type: 'text' },
    { name: 'uuid', label: 'uuid', type: 'text' },
    { 
      name: 'money_raised',
      label: '募資金額',
      type: 'object',
      fields: [
        { name: 'value_usd', label: 'value_usd' },
        { name: 'currency', label: 'currency' },
        { name: 'value', label: 'value' }
      ]
    }
  ];

  // 定義巢狀欄位（lead_investors 的結構）
  const leadInvestorFields = [
    { name: 'image', label: 'image' },
    { name: 'name', label: 'name' },
    { name: 'permalink', label: 'permalink' }
  ];

  return (
    <div className="space-y-8">
      <div>
        <CustomLabels texts="funding_rounds_list" />
        <NestedCardsGroup
          register={register}
          errors={errors}
          setValue={setValue}
          watch={watch}
          groupName="funding_rounds_list"
          fields={fundingRoundFields}
          nestedFields={leadInvestorFields}
          fieldTitle="funding_rounds"
          nestedFieldTitle="funding_rounds"
        />
      </div>
    </div>
  );
};

const Step6Form = ({ register, errors, setValue, watch }) => {
  // 投資欄位定義
  const investmentFields = [
    { name: 'announced_on', label: 'announced_on', type: 'text' },
    { 
      name: 'organization', 
      label: '組織', 
      type: 'object',
      fields: [
        { name: 'id', label: 'id', type: 'text' },
        { name: 'image_id', label: 'image_id', type: 'text' },
        { name: 'type', label: 'type', type: 'text' },
        { name: 'value', label: 'value', type: 'text' }
      ]
    },
    { 
      name: 'funding_round', 
      label: '募資輪次', 
      type: 'object',
      fields: [
        { name: 'id', label: 'id', type: 'text' },
        { name: 'image_id', label: 'image_id', type: 'text' },
        { name: 'type', label: 'type', type: 'text' },
        { name: 'value', label: 'value', type: 'text' }
      ]
    },
    { name: 'id', label: 'id', type: 'text' },
    { name: 'type', label: 'type', type: 'text' },
    { name: 'value', label: 'value', type: 'text' },
    { name: 'lead_investor', label: 'lead_investor', type: 'boolean' }
  ];

  return (
    <div className="space-y-8">
      <div>
        <CustomLabels texts="investments" />
        <NestedCardsGroup
          register={register}
          errors={errors}
          setValue={setValue}
          watch={watch}
          groupName="investments"
          fields={investmentFields}
          fieldTitle="investments"
        />
      </div>
    </div>
  );
};

const Step7Form = ({ register, errors, setValue, watch }) => {
  // 多元投資欄位定義
  const diversityInvestmentFields = [
    { name: 'announced_on', label: 'announced_on', type: 'text' },
    { 
      name: 'organization', 
      label: '組織', 
      type: 'object',
      fields: [
        { name: 'id', label: 'id', type: 'text' },
        { name: 'image_id', label: 'image_id', type: 'text' },
        { name: 'type', label: 'type', type: 'text' },
        { name: 'value', label: 'value', type: 'text' }
      ]
    },
    { 
      name: 'funding_round', 
      label: '募資輪次', 
      type: 'object',
      fields: [
        { name: 'id', label: 'id', type: 'text' },
        { name: 'image_id', label: 'image_id', type: 'text' },
        { name: 'type', label: 'type', type: 'text' },
        { name: 'value', label: 'value', type: 'text' }
      ]
    },
    { name: 'id', label: 'id', type: 'text' },
    { name: 'type', label: 'type', type: 'text' },
    { name: 'value', label: 'value', type: 'text' }
  ];

  return (
    <div className="space-y-8">
      <div>
        <CustomLabels texts="diversity_investments" />
        <NestedCardsGroup
          register={register}
          errors={errors}
          setValue={setValue}
          watch={watch}
          groupName="diversity_investments"
          fields={diversityInvestmentFields}
          fieldTitle="diversity_investments"
        />
      </div>
    </div>
  );
};

const Step8Form = ({ register, errors, setValue, watch }) => {
  const acquisitionFields = [
    { 
      name: 'identifier', 
      label: 'identifier',
      type: 'object',
      fields: [
        { name: 'entity_def_id', label: 'entity_def_id', type: 'text' },
        { name: 'image_id', label: 'image_id', type: 'text' },
        { name: 'permalink', label: 'permalink', type: 'text' },
        { name: 'uuid', label: 'uuid', type: 'text' },
        { name: 'value', label: 'value', type: 'text' }
      ]
    },
    { 
      name: 'price', 
      label: 'price',
      type: 'object',
      fields: [
        { name: 'value_usd', label: 'value_usd', type: 'text' },
        { name: 'value', label: 'value', type: 'text' }
      ]
    },
    { 
      name: 'announced_on', 
      label: 'announced_on',
      type: 'object',
      fields: [
        { name: 'value', label: 'value', type: 'text' }
      ]
    },
    { 
      name: 'acquiree_identifier', 
      label: 'acquiree_identifier',
      type: 'object',
      fields: [
        { name: 'role', label: 'role', type: 'text' },
        { name: 'entity_def_id', label: 'entity_def_id', type: 'text' },
        { name: 'image_id', label: 'image_id', type: 'text' },
        { name: 'permalink', label: 'permalink', type: 'text' },
        { name: 'uuid', label: 'uuid', type: 'text' },
        { name: 'value', label: 'value', type: 'text' }
      ]
    }
  ];

  return (
    <div className="space-y-8">
      <div>
        <CustomLabels texts="acquisitions" />
        <NestedCardsGroup
          register={register}
          errors={errors}
          setValue={setValue}
          watch={watch}
          groupName="acquisitions"
          fields={acquisitionFields}
          fieldTitle="acquisitions"
        />
      </div>
    </div>
  );
};

const Step9Form = ({ register, errors, setValue, watch }) => {
  const exitFields = [
    { name: 'image', label: 'image' },
    { name: 'link', label: 'link' },
    { name: 'description', label: 'description' },
    { name: 'value', label: 'value' }
  ];

  const featuredListFields = [
    { name: 'title', label: 'title' },
    { name: 'org_num', label: 'org_num' },
    { name: 'org_num_investors', label: 'org_num_investors' },
    { 
      name: 'org_funding_total', 
      label: 'org_funding_total',
      type: 'object',
      fields: [
        { name: 'currency', label: 'currency' },
        { name: 'value', label: 'value' },
        { name: 'value_usd', label: 'value_usd' }
      ]
    }
  ];

  const contactFields = [
    { name: 'name', label: 'name' },
    { name: 'job_title', label: 'job_title' },
    { name: 'linkedin_id', label: 'linkedin_id' },
    { name: 'departments', label: 'departments', type: 'array' },
    { name: 'levels', label: 'levels', type: 'array' }
  ];

  const financialsFields = [
    { name: 'num_lead_investors', label: 'num_lead_investors' },
    { name: 'num_investors', label: 'num_investors' },
    { name: 'num_funding_rounds', label: 'num_funding_rounds' }
  ];

  return (
    <div className="space-y-8">
      <div className='border-b pb-8 border-gray-200'>
        <CustomLabels texts="exits" />
        <CardsGroup
          register={register}
          errors={errors}
          setValue={setValue}
          watch={watch}
          groupName="exits"
          fields={exitFields}
          fieldTitle="exits"
        />
      </div>

      <div className='border-b pb-8 border-gray-200'>
        <CustomLabels texts="featured_list" />
        <NestedCardsGroup
          register={register}
          errors={errors}
          setValue={setValue}
          watch={watch}
          groupName="featured_list"
          fields={featuredListFields}
          fieldTitle="featured_list"
        />
      </div>

      <div className='border-b pb-8 border-gray-200'>
        <CustomLabels texts="contacts" />
        <ArrayCardsGroup
            register={register}
            errors={errors}
            setValue={setValue}
            watch={watch}
            groupName="contacts"
            fields={contactFields}
            fieldTitle="contacts"
        />
      </div>
      
      <div>
        <CustomLabels texts="financials_highlights" />
        <CardsGroup
          register={register}
          errors={errors}
          setValue={setValue}
          watch={watch}
          groupName="financials_highlights"
          fields={financialsFields}
          fieldTitle="financials_highlights"
          userCanAdd={false}
        />
      </div>
    </div>
  );
};

const CompanyForm = () => {
  const Navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [mode, setMode] = useState('edit');
  const [companyData, setCompanyData] = useState(null);
  const { uuid } = useParams();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    trigger,
    watch,
    setValue,
    reset
  } = useForm({
    resolver: zodResolver(formSchema),
    mode: 'onChange', // 即時驗證
    defaultValues: mode === "edit" ? {} : {
      "location": [
        {
          name: '',
          permalink: ''
        }
      ],
      "industries": [
        {
          id: '',
          value: ''
        }
      ],
      "founders": [
        {
          value: ''
        }
      ],
      "headquarters_regions": [
        {
            id: "",
            value: ""
        }
      ],
      "funding_rounds_list": [
        {
          announced_on: '',
          lead_investors: [
            {
              image: '',
              name: '',
              permalink: ''
            }
          ],
          id: '',
          image_id: '',
          title: '',
          uuid: '',
          money_raised: {
            value_usd: '',
            currency: '',
            value: ''
          }
        }
      ]
    }
  });

  // 根據當前步驟取得對應的驗證 schema
  const getCurrentSchema = (step) => {
    switch (step) {
      case 1:
        return step1Schema;
      case 2:
        return step2Schema;
      case 3:
        return step3Schema;
      case 4:
        return step4Schema;
      case 5:
        return step5Schema;
      case 6:
        return step6Schema;
      case 7:
        return step7Schema;
      case 8:
        return step8Schema;
      case 9:
        return step9Schema;
      default:
        return step1Schema;
    }
  };

  // 處理「下一步」
  const handleNext = async () => {
    // 取得當前步驟需要驗證的欄位
    const currentSchema = getCurrentSchema(currentStep);
    const fields = Object.keys(currentSchema.shape);

    // 驗證當前頁面的欄位
    const isValid = await trigger(fields);

    if (isValid) {
      setCurrentStep(prev => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  // 處理「上一步」
  const handlePrev = () => {
    setCurrentStep(prev => prev - 1);
    window.scrollTo(0, 0);
  };

  // 處理表單提交
  const onSubmit = async (data) => {
    try {
      let res;
      if (mode === "edit") {
        res = await NewCompanyService.update(uuid, data);
        showToast.success('公司資料更新成功');
      } else {
        res = await NewCompanyService.create(data);
        showToast.success('公司建立成功');
      }

      setTimeout(() => {
        Navigate(`/company`);
      }, 1000);
    } catch (error) {
      console.error('表單提交失敗:', error);
      showToast.error('公司建立失敗');
    }
  };

  // 監聽所有欄位
  const formValues = watch();

  useEffect(() => {
    const fetchCompanyData = async () => {
      if (uuid.trim() === "new") {
        setMode('create');
        return;
      }
      try {
        setMode('edit');
        const data = await NewCompanyService.getDetails(uuid);
        setCompanyData(data?.data);
        // 使用 setValue 為每個字段設置值
        Object.keys(data?.data).forEach(key => {
          setValue(key, data?.data[key]);
        });
        // reset(data); // 使用 reset 設置表單數據
      } catch (error) {
        console.error('Failed to fetch company data:', error?.response?.data);
        if (error?.response?.status === 401 || error?.response?.status === 403) {
          showToast.error('無權訪問此頁面');
          return;
        }
        if (error?.response?.status === 400 && error?.response?.data?.errorCode === "2000") {
          Navigate(`/company`);
          return;
        }
        showToast.error('獲取公司資料失敗，請稍後再試');
      }
    }

    fetchCompanyData();
  }, [uuid, setValue, reset]);

  // 監聽多個特定欄位
  // const watchSpecificFields = watch(['name', 'email', 'phone']);

  // 使用 watch 的回調函數方式
  // useEffect(() => {
    // const subscription = watch((value, { name, type }) => {
    //   console.log('變更的欄位:', name);
    //   console.log('變更類型:', type);
    //   console.log('目前的值:', value);
    // });

    // 清理訂閱
    // return () => subscription.unsubscribe();
  // }, [watch]);

  return (
    <div className="container mx-auto p-6">
      <div className="mb-8">
        <div className="flex justify-between items-center relative">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((step) => (
            <div
              key={step}
              className={`w-8 h-8 rounded-full flex items-center justify-center cursor-pointer ${
                currentStep >= step ? 'bg-blue-500 text-white' : 'bg-gray-200'
              }`}
              onClick={() => setCurrentStep(step)}
            >
              {step}
            </div>
          ))}
          <div className='absolute top-1/2 -translate-y-full w-full h-[1px] bg-gray-200 -z-10'></div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {currentStep === 1 && <Step1Form register={register} errors={errors} />}
        {currentStep === 2 && <Step2Form register={register} errors={errors} />}
        {currentStep === 3 && <Step3Form register={register} errors={errors} />}
        {currentStep === 4 && <Step4Form register={register} errors={errors} setValue={setValue} watch={watch} />}
        {currentStep === 5 && <Step5Form register={register} errors={errors} setValue={setValue} watch={watch} />}
        {currentStep === 6 && <Step6Form register={register} errors={errors} setValue={setValue} watch={watch} />}
        {currentStep === 7 && <Step7Form register={register} errors={errors} setValue={setValue} watch={watch} />}
        {currentStep === 8 && <Step8Form register={register} errors={errors} setValue={setValue} watch={watch} />}
        {currentStep === 9 && <Step9Form register={register} errors={errors} setValue={setValue} watch={watch} />}

        <div className={`flex ${currentStep === 1 ? 'justify-end' : 'justify-between'} mt-8`}>
          {currentStep > 1 && (
            <button
              type="button"
              onClick={handlePrev}
              className="px-4 py-2 border border-gray-600 text-gray-600 rounded hover:bg-gray-50"
            >
              上一頁
            </button>
          )}
          
          <div className='flex items-center'>
            {currentStep < 9 && (
              <button
                type="button"
                onClick={handleNext}
                className="px-4 py-2 border border-blue-500 text-blue-500 rounded hover:bg-blue-50"
              >
                繼續填寫
              </button>
            )}
          
            <button
              type="submit"
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 ml-4"
              disabled={isSubmitting}
            >
              {isSubmitting ? "提交中..." : "提交表單"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CompanyForm;